import { useEffect } from "react";
import {
  StatsContainer,
  StatsCardLoading,
  StatsCard,
} from "./components/Stats";
import { Workspace } from "./types";
import useCalculateUsage from "./useCalculateUsage";

export default Usage;

function calculateAvailableStat(total: number, used: number) {
  return total - used;
}

function calculateStatProgressWidth(total: number, used: number) {
  if (total === 0 && used === 0) {
    return 0;
  }

  if (total < used) {
    return 100;
  }

  const value = Math.floor((used / total) * 100);

  return value;
}

interface UsageProps {
  isLoading: boolean;
  workspaces: Array<Workspace>;
  activeWorkspaceIdx: number;
  assistants: Array<object>;
  workspacesAllowed: number | undefined;
  assistantsPerWorkspace: number | undefined;
}

function Usage(props: UsageProps) {
  const {
    isLoading,
    workspaces,
    activeWorkspaceIdx,
    workspacesAllowed,
    assistants,
    assistantsPerWorkspace,
  } = props;

  const usageLoading = (
    <StatsContainer>
      <StatsCardLoading />
      <StatsCardLoading />
      <StatsCardLoading />
      <StatsCardLoading />
    </StatsContainer>
  );

  const isWorkspaceLoading = isLoading || activeWorkspaceIdx === undefined || workspaces.length === 0;

  return isWorkspaceLoading ? (
    usageLoading
  ) : (
    <StatsContainer>
      <CallsAndQuestions workspace={workspaces[activeWorkspaceIdx]} />
      <WorkspacesAllowed
        totalWorkspaces={workspaces.length}
        workspacesAllowed={workspacesAllowed}
      />
      <AssistantsPerWorkspace
        assistantsPerWorkspace={assistantsPerWorkspace}
        assistants={assistants}
      />
    </StatsContainer>
  );
}

interface CallsAndQuestionsProps {
  workspace: Workspace;
}

function CallsAndQuestions(props: CallsAndQuestionsProps) {
  const { workspace } = props;

  const { callTime, questions, voiceSecLimit, allowedQuestions } =
    useCalculateUsage(workspace);

  const callTimeStat = (
    <StatsCard
      title="Calls"
      total={Math.ceil(voiceSecLimit / 60)}
      used={Math.ceil(callTime / 60)}
      calculateAvailable={calculateAvailableStat}
      calculateWidth={calculateStatProgressWidth}
      unit="mins"
    />
  );

  const messagesStat = (
    <StatsCard
      title="Messages"
      total={allowedQuestions}
      used={questions}
      calculateAvailable={calculateAvailableStat}
      calculateWidth={calculateStatProgressWidth}
      unit="Messagess"
    />
  );

  return (
    <>
      {callTimeStat}
      {messagesStat}
    </>
  );
}

interface WorkspacesAllowedProps {
  workspacesAllowed: number | undefined;
  totalWorkspaces: number;
}

function WorkspacesAllowed(props: WorkspacesAllowedProps) {
  const { workspacesAllowed, totalWorkspaces } = props;

  if (!workspacesAllowed) {
    return null;
  }

  return (
    <StatsCard
      title="Workspaces"
      total={workspacesAllowed}
      used={totalWorkspaces}
      calculateAvailable={calculateAvailableStat}
      calculateWidth={calculateStatProgressWidth}
      unit="Workspace"
    />
  );
}

interface AssistantsPerWorkspaceProps {
  assistantsPerWorkspace: number;
  assistants: Array<object>;
}

function AssistantsPerWorkspace(props: AssistantsPerWorkspaceProps) {
  const { assistantsPerWorkspace, assistants } = props;

  if (!assistantsPerWorkspace) {
    return null;
  }

  return (
    <StatsCard
      title="Assistants"
      total={assistantsPerWorkspace}
      used={assistants.length}
      calculateAvailable={calculateAvailableStat}
      calculateWidth={calculateStatProgressWidth}
      unit="Assistants"
    />
  );
}
